import type { GetStaticPaths, GetStaticProps } from 'next';
import type { SinglePartner, SinglePartnerDetails } from 'lib/queries/partners';
import type { ImageProps } from 'next/image';
import dynamic from 'next/dynamic';
import { Card, Link, Paragraph, Wrap, Button } from '@leafwell/components';
import PageWrap from 'components/page-wrap';
import HeadingBody from 'components/heading-body';
import LocationsMap from 'components/locations-map';
import Blocks from 'components/blocks';
import Rating from 'components/rating';
import LocationCard from 'components/location-card';
import TrustpilotCarousel from 'components/trustpilot-carousel';
import { getSinglePartner } from 'lib/queries/partners';
import { LocationItems } from 'lib/queries/locations';
import { useWordPressContext } from 'contexts/wordpress-provider';
import Image from 'next/image';

const LocationOpeningHours = dynamic(
  () => import('components/location-opening-hours'),
  {
    ssr: false,
  },
);
const SignupFormTelehealth = dynamic(
  () => import('components/signup-form-telehealth'),
  {
    ssr: false,
  },
);

const ContactsLink: React.FC<{
  href: string;
  icon: ImageProps;
  title: string;
}> = ({ href, icon, title }) => (
  <Link
    className="font-medium flex gap-3 group"
    href={href}
    rel="noopener noreferrer"
    target="_blank"
  >
    <Image
      {...icon}
      className="w-5 transition-opacity group-hover:opacity-50"
    />
    {title}
  </Link>
);

const SinglePartnerHeader: React.FC<
  SinglePartnerDetails & {
    name: string;
    isStatic?: boolean;
  }
> = ({
  name,
  formattedAddress,
  latitude,
  longitude,
  openingHours,
  internationalPhoneNumber,
  placeId,
  rating,
  state,
  userRatingsTotal,
  isStatic = false,
}) => {
  const encodedAddress = encodeURIComponent(formattedAddress);
  const { translations } = useWordPressContext();
  const lat = Number(latitude);
  const lng = Number(longitude);

  return (
    <>
      <Wrap className="!gap-y-0" size="large" tagName="header">
        <Paragraph variant="medium">{translations[state]}</Paragraph>
        <HeadingBody level="2" tagName="h1" title={name} />
      </Wrap>
      <Wrap
        className="lg:grid-cols-2 lg:gap-x-10 xl:gap-x-16 !pt-0"
        size="large"
      >
        <Card
          className="content-start sm:grid-cols-inline-form"
          rounded={true}
          variant="default"
        >
          <HeadingBody level="4" title={translations['Partner details']} />
          {rating && Number(rating) > 0 ? (
            <div className="grid sm:justify-end">
              <Rating rating={rating} name={name} showLabel={true} />
              <Link
                className="text-sm"
                href={`https://www.google.com/maps/search/?api=1&query=${encodedAddress}&query_place_id=${placeId}`}
                rel="noopener noreferrer"
                target="_blank"
                variant="underline"
              >{`${userRatingsTotal} ${translations['Google reviews']}`}</Link>
            </div>
          ) : null}
          <p className="col-span-full grid sm:flex gap-x-3">
            <Paragraph className="font-medium shrink-0" tagName="span">
              {translations['Address']}
            </Paragraph>
            <Paragraph tagName="span" variant="medium">
              {formattedAddress}
            </Paragraph>
          </p>
          {internationalPhoneNumber && (
            <p className="col-span-full grid sm:flex gap-x-3">
              <Paragraph className="font-medium shrink-0" tagName="span">
                {translations['Phone']}
              </Paragraph>
              <Paragraph tagName="span" variant="medium">
                {internationalPhoneNumber}
              </Paragraph>
            </p>
          )}
          {openingHours && (
            <section className="col-span-full grid sm:flex items-baseline gap-3 gap-y-2">
              <Paragraph className="font-medium" tagName="span">
                {translations['Opening hours']}
              </Paragraph>
              <LocationOpeningHours
                openingHours={openingHours}
                isStatic={isStatic}
              />
            </section>
          )}
          {placeId && (
            <section className="col-span-full border-t pt-6 flex gap-6 xl:gap-12">
              <ContactsLink
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&destination_place_id=${placeId}`}
                icon={{
                  alt: '',
                  src: '/directions.svg',
                  height: '17',
                  width: '18',
                }}
                title={translations['Get directions']}
              />
              <ContactsLink
                href={`tel:${internationalPhoneNumber}`}
                icon={{
                  alt: '',
                  src: '/call.svg',
                  height: '18',
                  width: '19',
                }}
                title={translations['Call']}
              />
            </section>
          )}
          <LocationsMap
            center={{ lat, lng }}
            className="col-span-full rounded-lg overflow-hidden h-64"
            locations={[{ lat, lng }]}
          />
        </Card>
        <Card filled={true} rounded={true} tagName="section" variant="default">
          <HeadingBody
            level="4"
            title={translations['Get your medical marijuana card']}
          />
          <SignupFormTelehealth
            src={''}
            utmParams={null}
            stateCode={state}
            isCardForm={true}
            readOnlyStateField={true}
            redirectOnStateChange={false}
          />
        </Card>
      </Wrap>
    </>
  );
};

export type NearbyLocationsProps = {
  nearbyLocations?: LocationItems;
};
const NearbyLocations: React.FC<NearbyLocationsProps> = ({
  nearbyLocations,
}) => {
  const { translations, contactsPage, partnerMapPage } = useWordPressContext();
  const hasLocations = nearbyLocations?.length > 0;

  return hasLocations ? (
    <Wrap size="large">
      <div className="grid gap-y-0">
        <HeadingBody level="4" title={translations['Nearby locations']} />
        <Link
          className="text-lg flex pt-2 lg:pt-0 lg:justify-end underline hover:cursor-pointer "
          href={partnerMapPage.uri}
        >
          {translations['View more locations']}
        </Link>
      </div>
      <div className="flex flex-wrap justify-center md:justify-start gap-7">
        {hasLocations &&
          nearbyLocations.map(
            ({
              id,
              name,
              formattedAddress,
              state,
              rating,
              uri,
              openingHours,
            }) => {
              return (
                <LocationCard
                  key={id}
                  id={id}
                  name={name}
                  formattedAddress={formattedAddress}
                  state={state}
                  rating={rating}
                  uri={uri}
                  openingHours={openingHours}
                  layout="vertical"
                />
              );
            },
          )}
        <Card
          rounded={true}
          variant="light"
          filled={false}
          className="w-64 md:w-68 lg:w-72 xl:w-80"
          style={{
            backgroundImage: `url('/card-background.svg')`,
          }}
        >
          <span className="flex flex-col items-center justify-center">
            <HeadingBody
              level="4"
              title={translations['Can’t find a store near you?']}
              className="text-center"
            />
            <Button
              variant="light"
              label={translations['Let us know']}
              rounded={true}
              className="mt-8"
              href={contactsPage.uri}
            />
          </span>
        </Card>
      </div>
    </Wrap>
  ) : null;
};

const SinglePartner: React.FC<SinglePartner> = ({
  nearbyLocations,
  isStatic,
  partner: {
    name,
    seo,
    acf: { blocks },
    details,
  },
}) => {
  return (
    <PageWrap seo={seo} title={name} pageType="partner">
      <SinglePartnerHeader {...details} name={name} isStatic={isStatic} />
      <TrustpilotCarousel />
      <Blocks blocks={blocks} />
      <NearbyLocations nearbyLocations={nearbyLocations} />
    </PageWrap>
  );
};

export const getStaticProps: GetStaticProps = async context => {
  return await getSinglePartner(context);
};

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' };
};

export default SinglePartner;
